import {
  combineRefs,
  DomElementAlignment,
  DomTargetPosition,
  IconButton,
  OdinIcon,
  OdinIconSize,
  Tooltip,
  useTestTag,
} from '@myosh/odin-components';
import type { DataGridRef, DropDownResult, OdinDataRetrievalOptions } from '@myosh/odin-components';
import clsx from 'clsx';
import { sortBy } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { Subject, takeUntil } from 'rxjs';
import type { FormPermissionsResults } from '../../../@types/form-permissions';
import type { Module } from '../../../@types/modules';
import type { RecordListMenuItem, RecordListMenuItemValue } from '../../../@types/record-list-menu';
import useAddonQuery from '../../../hooks/use-addon-query';
import useAIFlagEnabled, { RecordAIEnabledKey } from '../../../hooks/use-ai-flag-enabled';
import useProfileData from '../../../hooks/use-profile-data';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { useGetFeatureToursQuery } from '../../../redux/services/api';
import {
  useGetPinnedRecordListMenuItemsQuery,
  usePinRecordListMenuItemMutation,
  useUnpinRecordListMenuItemMutation,
} from '../../../redux/services/record-list-menu';
import { useGetFormAddOnsQuery } from '../../../redux/services/view';
import { submitAnalyzeRecordsJob, submitExportRecordsJob } from '../../../redux/slices/background-job-slice';
import { selectedGlobalHierarchies, selectedMatchingOption } from '../../../redux/slices/global-hierarchies';
import { getDataGridReference } from '../../../services/data-grid.service';
import {
  getVisibleColumnsForFileExport,
  getVisibleColumnsForFileExportAllForms,
} from '../../../utils/dynamic-page-utils';
import { buildGlobalHierarchyFiltersUrl } from '../../../utils/filter-url-builder-utils';
import { buildSortedFieldsUrlString } from '../../../utils/sort-url-builder-utils';
import FormSettingsFeatureTour from '../../layout/components/feature-tour-guides/form-settings-tour';
import { convertToFieldFilters, convertToSortedFields, RecordListMenuItemType } from './data-grid-menu-utils';
import type { DataGridSettingsMenuItem } from './data-grid-menu-utils';
import AnalyzeRecordsModal from './modals/analyze-records-modal.component';
import ExportFullRecordDataModal from './modals/export-full-record-data-modal.component';
import ExportRecordListTemplatesModal from './modals/export-record-list-templates-modal.component';
import ImportAttachmentsModal from './modals/import-attachments-modal.component';
import ImportRecordsModal from './modals/import-records-modal.component';
import ManageViewsModal from './modals/manage-views-modal.component';
import SaveViewModal from './modals/save-view-modal.component';
import PinnableMenu from './pinnable-menu.component';

interface DataGridSettingsMenuProps {
  formPermissions?: FormPermissionsResults;
  isAllForms: boolean;
  selectedForm: DropDownResult;
  module: Module;
  isMobile?: boolean;
  viewHasChanged?: boolean;
  getRecordsIds?: () => string;
  allFormsExportPermission?: boolean;
  isCalendarView?: boolean;
}

export default function DataGridMenu({
  formPermissions,
  selectedForm,
  isAllForms,
  viewHasChanged,
  module,
  isMobile,
  getRecordsIds,
  allFormsExportPermission,
  isCalendarView,
}: DataGridSettingsMenuProps) {
  const [showImportRecordsModal, setShowImportRecordsModal] = useState(false);
  const [showManageViewsModal, setShowManageViewsModal] = useState<boolean>(false);
  const [showSaveViewModal, setShowSaveViewModal] = useState(false);
  const [exportRecordsTemplatesModal, setExportRecordsTemplatesModal] = useState(false);
  const [isMoreOptionsActive, setIsMoreOptionsActive] = useState(false);
  const [isImportAttachmentsModalVisible, setIsImportAttachmentsModalVisible] = useState(false);
  const [isExportFullRecordsModalVisible, setIsExportFullRecordsModalVisible] = useState(false);
  const [isAnalyzeRecordsModalVisible, setIsAnalyzeRecordsModalVisible] = useState(false);

  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const globalFilters = useAppSelector(selectedGlobalHierarchies);
  const globalFiltersMatchingOption = useAppSelector(selectedMatchingOption);
  const queryParams = useAddonQuery();
  const { profileData: { user: userData } = {} } = useProfileData();
  const isSuperUser = userData?.superUser;

  const { pathname } = useLocation();
  const isRecordOpen = pathname.includes('/records');

  const gridRef = useRef<DataGridRef | undefined>(undefined);
  const destroySubject = useRef(new Subject<void>());
  const moreOptionsRef = useRef<HTMLDivElement>(null);
  const aiAnalysisEnabledRef = useRef<boolean>(false);
  const tagCreator = useTestTag('data-grid-menu');

  const getColumns = () => gridRef.current?.api.data.columns ?? [];
  const getFilters = () => gridRef.current?.api.filters.getFilters() ?? [];
  const getSortedFields = () => gridRef.current?.api.sortedFields.getSortedFields() ?? [];
  const convertedFilters = convertToFieldFilters(getFilters());
  const convertedSorts = convertToSortedFields(getSortedFields());
  const convertedSortDirective = convertToSortedFields(getSortedFields(), true);

  const onCloseImportRecordsModal = () => setShowImportRecordsModal(false);
  const onCloseExportRecordsTemplatesModal = () => setExportRecordsTemplatesModal(false);
  const onCloseManageViewsModal = () => setShowManageViewsModal(false);
  const onCloseSaveViewModal = () => setShowSaveViewModal(false);
  const onCloseExportFullRecordsModal = () => setIsExportFullRecordsModalVisible(false);
  const onCloseAnalyzeRecordsModal = () => setIsAnalyzeRecordsModalVisible(false);

  const resetView = () => {
    gridRef?.current?.api.data.resetView();
  };

  const gridOptions: OdinDataRetrievalOptions = {
    fieldFilters: convertedFilters,
    sortedFields: convertedSorts,
  };

  const { data: pinnedMenuItems } = useGetPinnedRecordListMenuItemsQuery();
  const { enabled: isAIAnalysisEnabled, isFetching } = useAIFlagEnabled(RecordAIEnabledKey);

  const [pinMenuItem] = usePinRecordListMenuItemMutation();
  const [unpinMenuItem] = useUnpinRecordListMenuItemMutation();

  const { data: addonsData, isFetching: areAddonsFetching } = useGetFormAddOnsQuery(selectedForm?.value as number, {
    skip: selectedForm === undefined || selectedForm.value === undefined || isAllForms === true,
  });

  const { data: featureTourData } = useGetFeatureToursQuery();

  useEffect(() => {
    if (!isFetching) {
      aiAnalysisEnabledRef.current = isAIAnalysisEnabled;
    }
  }, [isAIAnalysisEnabled, isFetching]);

  useEffect(() => {
    destroySubject.current = new Subject<void>();
    getDataGridReference()
      .pipe(takeUntil(destroySubject.current))
      .subscribe((dataGridRef) => {
        if (dataGridRef) {
          gridRef.current = dataGridRef;
        }
      });

    return () => {
      destroySubject.current?.next();
      destroySubject.current?.complete();
    };
  }, []);

  useEffect(() => {
    if (isMoreOptionsActive) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMoreOptionsActive]);

  const doExport = (event: string, allForms: boolean) => {
    const gridFilterAndSort: OdinDataRetrievalOptions = {
      fieldFilters: convertToFieldFilters(getFilters()),
      sortedFields: convertToSortedFields(getSortedFields()),
    };
    const sortDirective = buildSortedFieldsUrlString(
      convertedSortDirective,
      gridRef?.current?.api.data.columns,
      allForms
    );

    const sort = buildSortedFieldsUrlString(convertedSortDirective, gridRef?.current?.api.data.columns, allForms, true);

    const commonPayload = {
      format: event,
      formName: selectedForm?.text as string,
      columns: allForms
        ? getVisibleColumnsForFileExportAllForms(getColumns())
        : getVisibleColumnsForFileExport(getColumns()),
      gridOptions: gridFilterAndSort,
      globalHierarchyFilters: buildGlobalHierarchyFiltersUrl(globalFilters, globalFiltersMatchingOption),
      allForms,
    };

    const specificPayload = allForms
      ? { moduleId: module.id, formName: selectedForm?.text as string, sortDirective: sort }
      : { formId: selectedForm?.value as string, moduleId: undefined, sortDirective };

    dispatch(submitExportRecordsJob({ ...commonPayload, ...specificPayload }));
  };

  const doAnalyze = useCallback(
    (allForms: boolean, extraPrompt?: string) => {
      const gridFilterAndSort: OdinDataRetrievalOptions = {
        fieldFilters: convertToFieldFilters(getFilters()),
        sortedFields: convertToSortedFields(getSortedFields()),
      };

      const payload = {
        format: 'CSV',
        formName: selectedForm?.text as string,
        formId: selectedForm?.value as string,
        columns: allForms
          ? getVisibleColumnsForFileExportAllForms(getColumns())
          : getVisibleColumnsForFileExport(getColumns()),
        gridOptions: gridFilterAndSort,
        moduleId: module.id,
        allForms,
        viewId: module.viewId,
        extraPrompt,
      };

      dispatch(submitAnalyzeRecordsJob(payload));
    },
    [selectedForm, module.id, module.viewId, dispatch]
  );

  const handleAnalyzeRecordsClick = useCallback(
    (data: string) => {
      doAnalyze(isAllForms, data);
    },
    [doAnalyze, isAllForms]
  );

  const onPinMenuItemClick = (key: RecordListMenuItemValue, isPinned: boolean, addonId?: number) => {
    const updatePinStatus = (item: RecordListMenuItem) => (isPinned ? unpinMenuItem(item) : pinMenuItem(item));

    updatePinStatus({
      value: key,
      addonId,
    });
  };

  const isMenuItemPinned = (key: RecordListMenuItemType) => {
    if (!pinnedMenuItems) {
      return false;
    }

    return pinnedMenuItems.some((item) => item.value === key);
  };

  const handleMenuToggle = () => {
    setIsMoreOptionsActive((prev) => !prev);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      moreOptionsRef.current &&
      !moreOptionsRef.current.contains(event.target as Node) &&
      featureTourData?.hasFormSettingsTourRan
    ) {
      setIsMoreOptionsActive(false);
    }
  };

  const hasExportRecordPermissions = isAllForms ? allFormsExportPermission : !!formPermissions?.canExportRecord;

  const menuItems: Array<DataGridSettingsMenuItem> = [
    {
      label: t('restore-current-view'),
      key: RecordListMenuItemType.Restore,
      icon: 'Undo2',
      onClick: resetView,
      disabled: !viewHasChanged,
      isPinned: isMenuItemPinned(RecordListMenuItemType.Restore),
      isPinIconHidden: isMobile,
      visible: true,
      className: 'restore-view', // restore-view is used for Feature Tours
    },
    {
      label: t('save-current-view'),
      key: RecordListMenuItemType.SaveView,
      icon: 'Save',
      onClick: () => setShowSaveViewModal(true),
      isPinned: isMenuItemPinned(RecordListMenuItemType.SaveView),
      isPinIconHidden: isMobile,
      visible: true,
      className: 'save-view', // save-view is used for Feature Tours
    },
    {
      label: t('manage-views'),
      key: RecordListMenuItemType.ManageView,
      icon: 'Columns3Cog',
      onClick: () => setShowManageViewsModal(true),
      visible: !isMobile,
      isPinned: isMenuItemPinned(RecordListMenuItemType.ManageView),
      isPinIconHidden: isMobile,
      className: 'manage-views', // manage-views is used for Feature Tours
    },
    {
      key: 'separator1',
      isSeparator: true,
    },
    {
      key: RecordListMenuItemType.ImportAttachments,
      label: t('import-attachments'),
      icon: 'Paperclip',
      onClick: () => setIsImportAttachmentsModalVisible(true),
      visible: !!formPermissions?.canImportRecord && !isAllForms,
      isPinned: isMenuItemPinned(RecordListMenuItemType.ImportAttachments),
      isPinIconHidden: isMobile,
    },
    {
      key: RecordListMenuItemType.Import,
      label: t('import-records'),
      icon: 'Upload',
      onClick: () => setShowImportRecordsModal(true),
      visible: !!formPermissions?.canImportRecord && !isAllForms && !isMobile,
      isPinned: isMenuItemPinned(RecordListMenuItemType.Import),
      isPinIconHidden: isMobile,
      className: 'import-records', // import-records is used for Feature Tours
    },
    {
      key: RecordListMenuItemType.Export,
      label: t('export'),
      icon: 'Download',
      isPinned: isMenuItemPinned(RecordListMenuItemType.Export),
      isPinIconHidden: isMobile,
      visible: !isSuperUser && hasExportRecordPermissions && !isCalendarView,
      className: 'export-records', // export-records is used for Feature Tours
      subMenuItems: [
        {
          label: t('record-list'),
          key: 'exportRecordListTitle',
          isPinIconHidden: true,
          className: 'font-bold',
          isTitle: true,
          titleDescription: 'Exports only the visible table columns',
        },
        {
          key: 'separator3',
          isSeparator: true,
        },
        {
          label: 'CSV',
          key: 'CSV',
          onClick: () => doExport('CSV', isAllForms),
          visible: !isAllForms,
          isPinIconHidden: true,
        },
        {
          label: 'Excel',
          key: 'EXCEL',
          onClick: () => doExport('EXCEL', isAllForms),
          visible: !!formPermissions?.canExportRecord || isAllForms,
          isPinIconHidden: true,
        },
        {
          label: 'PDF',
          key: 'PDF',
          onClick: () => doExport('PDF', isAllForms),
          visible: !isAllForms,
          isPinIconHidden: true,
        },
        {
          key: 'separator5',
          isSeparator: true,
          visible: !isAllForms,
        },
        {
          label: t('full-record-data'),
          key: 'exportRecordFullDataTitle',
          className: 'font-bold',
          isPinIconHidden: true,
          isTitle: true,
          visible: !isAllForms,
          titleDescription: 'Exports full record details',
        },
        {
          key: 'separator4',
          isSeparator: true,
          visible: !isAllForms,
        },
        {
          label: 'PDF',
          key: 'PDF',
          onClick: () => setIsExportFullRecordsModalVisible(true),
          visible: !isAllForms,
          isPinIconHidden: true,
        },
        {
          label: 'Excel',
          key: 'Excel',
          onClick: () => setExportRecordsTemplatesModal(true),
          visible: !isAllForms,
          isPinIconHidden: true,
        },
      ],
    },
    {
      key: 'separator2',
      isSeparator: true,
      visible: !isSuperUser && hasExportRecordPermissions && aiAnalysisEnabledRef.current,
    },
    {
      key: RecordListMenuItemType.AnalyzeRecord,
      label: t('analyse-records'),
      icon: 'FlaskConical',
      onClick: () => setIsAnalyzeRecordsModalVisible(true),
      visible: !isSuperUser && hasExportRecordPermissions && aiAnalysisEnabledRef.current,
      isPinned: isMenuItemPinned(RecordListMenuItemType.AnalyzeRecord),
      isPinIconHidden: isMobile,
    },
    {
      key: 'separator3',
      isSeparator: true,
      visible:
        !!addonsData &&
        addonsData.length > 0 &&
        !isAllForms &&
        (!!formPermissions?.canExportRecord || !!formPermissions?.canImportRecord),
    },
  ];

  const addOnItems = () => {
    if (isAllForms) {
      return [];
    }

    const isAddonPinned = (id: number) => {
      if (!pinnedMenuItems) {
        return false;
      }

      return pinnedMenuItems.some((item) => item.value === RecordListMenuItemType.Addon && item.addonId === id);
    };

    return (
      addonsData
        ?.filter(
          (addonItem) =>
            addonItem.success === true && addonItem.result.displayType === 'RECORD_LIST' && addonItem.result
        )
        .map((addonItem) => {
          const addOn = addonItem.result;
          //TODO recordsIds are yet to be implemented
          const records = getRecordsIds?.();
          const url = addOn.url + queryParams + (records ? `&records=${records}` : '');

          return {
            key: RecordListMenuItemType.Addon,
            addonId: addOn.id,
            label: addOn.caption,
            icon: addOn.icon ?? 'ExternalLink',
            url: url,
            target: addOn.target,
            visible: true,
            isPinned: isAddonPinned(addOn.id),
            isPinIconHidden: isMobile,
          } as DataGridSettingsMenuItem;
        }) ?? []
    );
  };

  const combinedMenuItems = [...menuItems, ...sortBy(addOnItems(), (item) => item.label)];
  const pinnedMenuItemOptions = (combinedMenuItems ?? []).filter((item) => item.isPinned && item.visible);
  const menuBaseStyles = 'py-1 text-sm rounded-md shadow-md bg-mono-1 absolute right-0 z-50';
  const menuStyles = clsx(menuBaseStyles, {
    hidden: !isMoreOptionsActive,
    'pin-feature': isMoreOptionsActive, // pin-feature is used for Feature Tours
  });
  const combinedRef = combineRefs(moreOptionsRef, tagCreator('more'));
  return (
    <>
      {pinnedMenuItemOptions.length > 0 && !isMobile && (
        <div className="flex gap-3">
          {pinnedMenuItemOptions.map((pinnedMenuItem, index) => {
            const pinnedMenuItemButtonStyle = clsx('grid place-content-center cursor-pointer', {
              'text-gray-3 pointer-events-none !cursor-default': pinnedMenuItem.disabled,
            });

            return (
              <Tooltip
                description={pinnedMenuItem.label}
                key={`${pinnedMenuItem.key}_${pinnedMenuItem.label}_${index}`}
                wrapperClassName="group/pinned-menu-item relative"
                tooltipAlignment={DomElementAlignment.BottomLeft}
                tooltipTargetPosition={DomTargetPosition.TopLeft}
              >
                <>
                  <a
                    className={pinnedMenuItemButtonStyle}
                    href={pinnedMenuItem.url}
                    target={pinnedMenuItem.target}
                    onClick={pinnedMenuItem.onClick}
                    tabIndex={0}
                  >
                    <OdinIcon size={OdinIconSize.Small} icon={pinnedMenuItem.icon ?? 'ExternalLink'} />
                  </a>
                  {pinnedMenuItem.subMenuItems && (
                    <div className={`${menuBaseStyles} top-full hidden pt-1 group-hover/pinned-menu-item:block`}>
                      <PinnableMenu items={pinnedMenuItem.subMenuItems} />
                    </div>
                  )}
                </>
              </Tooltip>
            );
          })}
        </div>
      )}
      <div ref={combinedRef} className="relative" onClick={handleMenuToggle}>
        {/*form-settings-menu is used for Feature Tours*/}
        <IconButton
          icon="Ellipsis"
          iconSize={OdinIconSize.Small}
          classNames="mr-2 h-9 grid place-content-center max-sm:mr-0 form-settings-menu"
        />
        <div className={menuStyles}>
          <PinnableMenu items={combinedMenuItems} onPinMenuItemClick={onPinMenuItemClick} isModalMenu={true} />
        </div>
      </div>
      <ImportRecordsModal
        selectedForm={selectedForm}
        columns={getVisibleColumnsForFileExport(gridRef?.current?.api.data.columns)}
        gridOptions={gridOptions}
        visible={showImportRecordsModal}
        onClose={onCloseImportRecordsModal}
      />
      <ExportRecordListTemplatesModal
        selectedForm={selectedForm}
        columns={getVisibleColumnsForFileExport(gridRef?.current?.api.data.columns)}
        gridOptions={gridOptions}
        visible={exportRecordsTemplatesModal}
        onClose={onCloseExportRecordsTemplatesModal}
      />
      <ExportFullRecordDataModal
        columns={gridRef?.current?.api.data.columns}
        selectedForm={selectedForm}
        gridOptions={gridOptions}
        visible={isExportFullRecordsModalVisible}
        onClose={onCloseExportFullRecordsModal}
        sortDirective={convertedSortDirective}
      />
      <SaveViewModal
        getColumns={getColumns}
        moduleId={module.id}
        selectedForm={selectedForm}
        visible={showSaveViewModal}
        onCloseModal={onCloseSaveViewModal}
        getFilters={getFilters}
        getSortedFields={getSortedFields}
        isAllForms={isAllForms}
      />
      <ManageViewsModal
        visible={showManageViewsModal}
        onClose={onCloseManageViewsModal}
        moduleId={module.id}
        selectedForm={selectedForm}
      />
      <ImportAttachmentsModal
        visible={isImportAttachmentsModalVisible}
        onClose={() => setIsImportAttachmentsModalVisible(false)}
      />
      <AnalyzeRecordsModal
        visible={isAnalyzeRecordsModalVisible}
        analyzeRecord={handleAnalyzeRecordsClick}
        onClose={onCloseAnalyzeRecordsModal}
      />
      {featureTourData &&
        !isRecordOpen &&
        !featureTourData.hasFormSettingsTourRan &&
        featureTourData.hasSettingsMenuTourRan &&
        !areAddonsFetching && (
          <FormSettingsFeatureTour
            isMenuOpen={isMoreOptionsActive}
            featureTourData={featureTourData}
            setIsMoreOptionsActive={setIsMoreOptionsActive}
          />
        )}
    </>
  );
}
